import {StateCreator} from "zustand/vanilla";
import {
  CompanyDetails,
  CompanySettings,
  CompanyUser,
  CompanyUserDetails,
  EnterpriseOrganization,
  OrganizationUserDetails,
  RoadmapDefaultView,
  User,
  UserCompanyInvite
} from "@/pb/api/devplan/types/company";
import {UserInfo, UserPreferences, UserTrialInfo} from "@/pb/api/devplan/types/user";
import {
  adminCompanySpecsEnabledAPI,
  companiesAPI,
  companyRoadmapDefaultAPI,
  companySettingsAPI,
  companyUserAPI,
  companyUsersAPI,
  companyUsersInvitesAPI,
  featureFlagsAPI,
  orgAPI,
  orgUsersAPI,
  plgCompaniesAPI,
  userAPI,
  userCompaniesInvitesAPI,
  userCompanyAPI,
  userInfoAPI,
  userPlansAPI,
  userPreferencesAPI,
  userUsageLimitsAPI
} from "@/app/apiPaths";
import {
  CreateCompanyRequest,
  CreateCompanyResponse,
  DeleteUserCompanyInviteResponse,
  DeleteUserFromCompanyResponse,
  GetCompanyInvitesResponse,
  GetCompanySettingsResponse,
  GetCompanyUsersResponse,
  SendCompanyInvitesRequest,
  SendCompanyInvitesResponse,
  SetRoadmapDefaultRequest,
  SetRoadmapDefaultResponse,
  SetSpecsEnabledRequest,
  SetSpecsEnabledResponse,
  UpdateUserCompanyRolesRequest,
  UpdateUserCompanyRolesResponse,
} from "@/pb/api/devplan/services/web/company";
import {
  GetFeatureFlagsResponse,
  GetOwnLimitsResponse,
  GetSelfResponse,
  GetUserInfoResponse,
  GetUserPlansResponse,
  GetUserPreferencesResponse,
  LeaveCompanyResponse,
  OwnInfo,
  ProcessCompanyInviteRequest,
  ProcessCompanyInviteResponse,
  UpdateUserInfoRequest,
  UpdateUserInfoResponse,
  UpdateUserPreferencesRequest,
  UpdateUserPreferencesResponse,
  UpdateUserRequest,
  UpdateUserResponse
} from "@/pb/api/devplan/services/web/user";
import logger from "@/app/logger";
import {InfoSection} from "@/pb/api/devplan/types/common";
import {deleteCompany, updateCompany} from "@/app/data/actions";
import {getUpdatesSections} from "@/app/components/company/utils";
import {CompanyRole} from "@/app/auth/roles";
import {CompanyLimits, UserLimits, UserPlan} from "@/pb/api/devplan/types/usage";
import {queryClient} from "@/hooks/queries/queryClient";
import {orgKeys, userKeys} from "@/hooks/queries/queryKeys";
import {FeatureFlags, PublicGlobalConfig} from "@/pb/api/devplan/types/config";
import {jr} from "@/hooks/queries/response-handler";
import {
  GetOrganizationResponse,
  GetOrganizationUsersResponse,
  UpdateOrganizationRequest,
  UpdateOrganizationResponse
} from "@/pb/api/devplan/services/web/organization";

export type UsersStoreState = {
  users: Record<string, User>;
  userInfo: UserInfo | undefined;
  userCompanyDetails: Record<number, Record<string, CompanyUserDetails>>;
  userOrgDetails: Record<string, Record<string, OrganizationUserDetails>>;
  userCompanyInvites: Record<number, UserCompanyInvite[]>;
  ownInvites: UserCompanyInvite[],
  ownCompanyUserDetails: CompanyUserDetails[],
  organizations: Record<string, EnterpriseOrganization>
  ownOrgDetails: OrganizationUserDetails[],
  ownLimits: UserLimits | undefined,
  ownCompanyLimits: Record<number, CompanyLimits>,
  ownTrialInfo: UserTrialInfo | undefined,
  userPreferences: UserPreferences | undefined,
  forceTrialModal: boolean,
  setForceTrialModal: (force: boolean) => void,
  publicConfig: PublicGlobalConfig | undefined,
  setUser(user: User): void;
  setUsers(users: User[]): void;
  fetchCompanyUsers(companyId: number): Promise<string[]>;
  fetchOrgUsers(orgId: string): Promise<number[]>;
  fetchSelf(): Promise<string>;
  fetchOwnLimits(): Promise<void>;
  getCompanyUsers(companyId: number): User[];
  getUser(id: string): User | undefined;
  updateUser(user: User): Promise<void>;
  updateUserCompanyRoles(userId: string, companyId: number, roles: CompanyRole[]): Promise<void>;
  deleteUserFromCompany(companyId: number, userId: string): Promise<void>;
  leaveCompany(companyId: number): Promise<void>;
  fetchCompanyInvites(companyId: number): Promise<void>;
  inviteCompanyUser(companyId: number, email: string, role: string): Promise<void>;
  deleteCompanyUserInvite(companyId: number, inviteEmail: string): Promise<void>;
  processCompanyUserInvitation(inviteId: string, accept: boolean): Promise<void>;
  fetchUserPreferences(): Promise<void>;
  updateUserPreferences(preferences: UserPreferences): Promise<void>;
  updateUserSection(userId: string, value: InfoSection): Promise<void>;
  fetchUserInfo(): Promise<void>;

  companies: Record<number, CompanyDetails>;
  companySettings: Record<number, CompanySettings>;
  setCompany(company: CompanyDetails): void;
  removeCompany(id: number): void;
  createCompany(company: CompanyDetails): Promise<CompanyDetails>;
  createPlgCompany(company: CompanyDetails, createSeparateForMatchingDomain?: boolean): Promise<CompanyDetails>;
  updateCompany(id: number, updater: (c: CompanyDetails) => CompanyDetails): Promise<CompanyDetails | undefined>;
  updateCompanySection(id: number, value: InfoSection): Promise<CompanyDetails | undefined>;
  deleteCompany(id: number): Promise<void>;
  fetchCompanySettings(id: number): Promise<void>;
  saveCompanyRoadmapDefault(companyId: number, roadmapDefault: RoadmapDefaultView): Promise<CompanySettings>;
  saveCompanySpecsEnabled(companyId: number, specsEnabled: boolean): Promise<CompanySettings>;

  deleteOrganization(id: string): Promise<void>;
  getOrganization(id: string): Promise<void>;
  updateOrganization(id: string, org: EnterpriseOrganization): Promise<void>;

  featureFlags: FeatureFlags | undefined;
  fetchFeatureFlags(): Promise<void>;

  plans: UserPlan[] | undefined;
  fetchPlans(): Promise<void>;
}

export const createUsersSlice: StateCreator<
  UsersStoreState,
  [],
  [],
  UsersStoreState
> = ((set, get) => ({
  users: {},
  userInfo: undefined,
  userCompanyDetails: {},
  userOrgDetails: {},
  userCompanyInvites: {},
  organizations: {},
  ownOrgDetails: [],
  ownInvites: [],
  ownCompanyUserDetails: [],
  ownLimits: undefined,
  ownCompanyLimits: {},
  ownTrialInfo: undefined,
  userPreferences: undefined,
  forceTrialModal: false,
  publicConfig: undefined,
  setForceTrialModal: v => set(s => ({...s, forceTrialModal: v})),
  setUser: user => set(s => ({...s, users: {...s.users, [user.id]: user}})),
  setUsers: updated => set(s => {
    const users = {...s.users}
    updated.forEach(u => users[u.id] = u)
    return {...s, users}
  }),
  fetchCompanyUsers: async (companyId) => {
    try {
      const resp = await fetch(companyUsersAPI(companyId))
      if (!resp.ok) {
        return Promise.reject(resp.statusText)
      }
      const {users} = GetCompanyUsersResponse.fromJSON(await resp.json())
      set(s => ({...s, ...setCompanyUsers(s, companyId, users)}))
      const companyDetails = users.reduce((a, u) => ({
        ...a,
        [u.user!.id]: u.companyDetails!
      }), {} as Record<string, CompanyUserDetails>)
      const updatedUsers = users.reduce((a, u) => ({...a, [u.user!.id]: u.user!}), {} as Record<string, User>)
      set(s => ({
        ...s,
        users: {...s.users, ...updatedUsers},
        userCompanyDetails: {...s.userCompanyDetails, [companyId]: companyDetails}
      }))
      return users.map(u => u.user!.id)
    } catch (error) {
      logger.error({error}, "Error fetching users")
      throw error
    }
  },
  fetchOrgUsers: async (orgId) => fetch(orgUsersAPI(orgId)).then(jr).then(js => {
    const {companyUsers, orgUsers} = GetOrganizationUsersResponse.fromJSON(js)
    const companyIds = Object.keys(companyUsers).map(v => Number(v))
    set(s => {
      let updatedState = {...s}
      companyIds.forEach(companyId => {
        updatedState = {...updatedState, ...setCompanyUsers(updatedState, companyId, companyUsers[companyId].users)}
      })
      const userOrgDetails: Record<string, OrganizationUserDetails> = {}
      const users: Record<string, User> = {}
      orgUsers.forEach(ou => {
        userOrgDetails[ou.user!.id] = ou.orgDetails!
        users[ou.user!.id] = ou.user!
      })
      updatedState = {
        ...updatedState,
        users: {...updatedState.users, ...users},
        userOrgDetails: {...updatedState.userOrgDetails, [orgId]: userOrgDetails}
      }
      return updatedState
    })
    return companyIds
  }),
  fetchSelf: async () => fetch(userAPI())
    .then(r => r.ok ? r.json() : Promise.reject(new Error(r.statusText)))
    .then(js => {
      const {ownInfo} = GetSelfResponse.fromJSON(js)
      set(s => ({...s, ...setOwnInfo(s, ownInfo!)}))
      return ownInfo!.user!.id
    }),
  fetchOwnLimits: async () => fetch(userUsageLimitsAPI())
    .then(r => r.ok ? r.json() : Promise.reject(new Error(r.statusText)))
    .then(js => {
      const {limits: ownLimits} = GetOwnLimitsResponse.fromJSON(js)
      set(s => ({...s, ownLimits}))
    }),
  getCompanyUsers: companyId => {
    const s = get()
    return Object.values(s.userCompanyDetails[companyId] || {}).map(d => s.users[d.userId]) || []
  },
  getUser: id => get().users[id],
  updateUser: async user => fetch(userAPI(), {
    method: 'POST', body: JSON.stringify(UpdateUserRequest.toJSON({user}))
  }).then(r => r.ok ? r.json() : Promise.reject(new Error(r.statusText)))
    .then(js => get().setUser(UpdateUserResponse.fromJSON(js).user!)),
  deleteUserFromCompany: async (companyId, userId) => fetch(companyUserAPI(companyId, userId), {method: 'DELETE'})
    .then(r => r.ok ? r.json() : Promise.reject(new Error(r.statusText)))
    .then(js => {
      const {users} = DeleteUserFromCompanyResponse.fromJSON(js)
      set(s => ({...s, ...setCompanyUsers(s, companyId, users)}))
    }),
  leaveCompany: async (companyId) => fetch(userCompanyAPI(companyId), {method: "DELETE"})
    .then(r => r.ok ? r.json() : Promise.reject(new Error(r.statusText)))
    .then(js => {
      const {ownInfo} = LeaveCompanyResponse.fromJSON(js)
      set(s => ({...s, ...setOwnInfo(s, ownInfo!)}))
    }),
  fetchCompanyInvites: async (companyId) => fetch(companyUsersInvitesAPI(companyId)).then(jr).then(js => {
    const {invites} = GetCompanyInvitesResponse.fromJSON(js)
    set(s => ({...s, userCompanyInvites: {...s.userCompanyInvites, [companyId]: invites}}))
  }),
  inviteCompanyUser: async (companyId, email, role) => fetch(companyUsersInvitesAPI(companyId),
    {method: "POST", body: JSON.stringify(SendCompanyInvitesRequest.toJSON({emails: [email], role}))})
    .then(jr)
    .then(js => {
      const {invites} = SendCompanyInvitesResponse.fromJSON(js)
      set(s => ({...s, userCompanyInvites: {...s.userCompanyInvites, [companyId]: invites}}))
    }),
  deleteCompanyUserInvite: async (companyId, inviteEmail) => fetch(companyUsersInvitesAPI(companyId),
    {method: "DELETE", body: JSON.stringify({email: inviteEmail})})
    .then(r => r.ok ? r.json() : Promise.reject(r.statusText))
    .then(js => {
      const {invites} = DeleteUserCompanyInviteResponse.fromJSON(js)
      set(s => ({...s, userCompanyInvites: {...s.userCompanyInvites, [companyId]: invites}}))
    }),
  fetchCompanySettings: async id => fetch(companySettingsAPI(id)).then(jr).then(js => {
    const {settings} = GetCompanySettingsResponse.fromJSON(js)
    set(s => ({...s, companySettings: {...s.companySettings, [id]: settings!}}))
  }),
  saveCompanyRoadmapDefault: async (companyId, roadmapDefault) => {
    const body = JSON.stringify(SetRoadmapDefaultRequest.toJSON({roadmapDefault}))
    const js = await fetch(companyRoadmapDefaultAPI(companyId), {method: "POST", body}).then(jr)
    const {settings} = SetRoadmapDefaultResponse.fromJSON(js)
    set(s => ({...s, companySettings: {...s.companySettings, [companyId]: settings!}}))
    return settings!
  },
  saveCompanySpecsEnabled: async (companyId, specsEnabled) => {
    const body = JSON.stringify(SetSpecsEnabledRequest.toJSON({specsEnabled}))
    const js = await fetch(adminCompanySpecsEnabledAPI(companyId), {method: "POST", body}).then(jr)
    const {settings} = SetSpecsEnabledResponse.fromJSON(js)
    set(s => ({...s, companySettings: {...s.companySettings, [companyId]: settings!}}))
    return settings!
  },
  processCompanyUserInvitation: async (inviteId, accept) => fetch(userCompaniesInvitesAPI(),
    {method: "POST", body: JSON.stringify(ProcessCompanyInviteRequest.toJSON({inviteId, accept}))})
    .then(r => r.ok ? r.json() : Promise.reject(r.statusText))
    .then(js => {
      const {ownInfo} = ProcessCompanyInviteResponse.fromJSON(js)
      set(s => ({...s, ...setOwnInfo(s, ownInfo!)}))
    }),
  updateUserCompanyRoles: async (userId, companyId, newRoles) => fetch(companyUserAPI(companyId, userId),
    {method: 'POST', body: JSON.stringify(UpdateUserCompanyRolesRequest.fromJSON({newRoles}))})
    .then(r => r.ok ? r.json() : Promise.reject(r.statusText))
    .then(js => {
      const {users} = UpdateUserCompanyRolesResponse.fromJSON(js)
      set(s => ({...s, ...setCompanyUsers(s, companyId, users)}))
    }),
  fetchUserPreferences: async () => await fetch(userPreferencesAPI())
    .then(r => r.ok ? r.json() : Promise.reject(r.statusText))
    .then(js => {
      const {preferences} = GetUserPreferencesResponse.fromJSON(js)
      set(s => ({...s, userPreferences: preferences}))
    }),
  updateUserPreferences: async preferences => fetch(userPreferencesAPI(),
    {method: 'POST', body: JSON.stringify(UpdateUserPreferencesRequest.toJSON({preferences}))}
  ).then(r => r.ok ? r.json() : Promise.reject(r.statusText))
    .then(js => {
      const {preferences} = UpdateUserPreferencesResponse.fromJSON(js)
      set(s => ({...s, userPreferences: preferences}))
    }),
  fetchUserInfo: async () => await fetch(userInfoAPI())
    .then(jr)
    .then(js => {
      set(s => ({...s, userInfo: GetUserInfoResponse.fromJSON(js).info}))
    }),
  updateUserSection: async (userId, section) => {
    const cached = get().userInfo ?? UserInfo.create({userId})
    const updated = {...cached, infoSections: getUpdatesSections(cached.infoSections, section)}
    await fetch(userInfoAPI(), {method: "POST", body: JSON.stringify(UpdateUserInfoRequest.toJSON({info: updated}))})
      .then(jr)
      .then(js => {
        const {info} = UpdateUserInfoResponse.fromJSON(js)
        set(s => ({...s, userInfo: info}))
      })
  },

  companies: {},
  companySettings: {},
  setCompany: async company => set(s => ({...s, companies: {...s.companies, [company.id]: company}})),
  removeCompany: (id) => set(s => {
    const companies = {...s.companies}
    delete companies[id]
    return {...s, companies}
  }),
  createCompany: async company => fetch(companiesAPI(),
    {method: "POST", body: JSON.stringify(CreateCompanyRequest.toJSON({company, createSeparateForMatchingDomain: false}))})
    .then(jr)
    .then(async js => {
      const {company: created} = CreateCompanyResponse.fromJSON(js)
      set(s => ({...s, companies: {...s.companies, [created!.id]: created!}}))
      await queryClient.invalidateQueries({queryKey: userKeys.limits()})
      if (company?.orgId) {
        void queryClient
          .invalidateQueries({queryKey: orgKeys.companies(company.orgId)})
          .then(() => get().fetchSelf())
      }
      return created!
    }),
  createPlgCompany: async (company, createSeparateForMatchingDomain = false) => fetch(plgCompaniesAPI(),
    {method: "POST", body: JSON.stringify(CreateCompanyRequest.toJSON({company, createSeparateForMatchingDomain}))})
    .then(jr)
    .then(async js => {
      const {company: created} = CreateCompanyResponse.fromJSON(js)
      set(s => ({...s, companies: {...s.companies, [created!.id]: created!}}))
      await get().fetchSelf()
      return created!
    }),
  updateCompany: async (id, updater) => {
    const company = get().companies[id]
    if (!company) {
      return company
    }
    const updated = updater(company)
    const fromDB = await updateCompany(id, updated)
    set(s => ({...s, companies: {...s.companies, [fromDB.id]: fromDB}}))
    return fromDB
  },
  updateCompanySection: async (id, section) => {
    return get().updateCompany(id, c => ({...c, infoSections: getUpdatesSections(c.infoSections, section)}))
  },
  deleteCompany: async id => {
    await deleteCompany(id)
    get().removeCompany(id)
  },
  deleteOrganization: async (id) => fetch(orgAPI(id), {method: "DELETE", body: "{}"}).then(jr).then(() => {
    set(s => {
      const organizations = {...s.organizations}
      delete organizations[id]
      return {...s, organizations}
    })
    void get().fetchSelf()
  }),
  getOrganization: async (id) => fetch(orgAPI(id)).then(jr).then(js => {
    const {org, orgCompanies} = GetOrganizationResponse.fromJSON(js)
    if (org) {
      set(s => {
        return {
          ...s,
          organizations: {...s.organizations, [id]: org!},
          companies: {...s.companies, ...Object.fromEntries(orgCompanies!.map(c => [c.id, c]))}
        }
      })
    }
  }),
  updateOrganization: async (id, org) => fetch(
    orgAPI(id), {method: "POST", body: JSON.stringify(UpdateOrganizationRequest.toJSON({org}))}
  ).then(jr).then((js) => {
    const {updated} = UpdateOrganizationResponse.fromJSON(js)
    set(s => ({...s, organizations: {...s.organizations, [id]: updated!}}))
  }),
  featureFlags: undefined,
  fetchFeatureFlags: async () => fetch(featureFlagsAPI())
    .then(r => r.ok ? r.json() : Promise.reject(r.statusText))
    .then(js => {
      const {featureFlags} = GetFeatureFlagsResponse.fromJSON(js)
      set(s => ({...s, featureFlags: featureFlags || FeatureFlags.create({})}))
    }),
  plans: undefined,
  fetchPlans: async () => fetch(userPlansAPI())
    .then(r => r.ok ? r.json() : Promise.reject(r.statusText))
    .then(js => {
      const {plans} = GetUserPlansResponse.fromJSON(js)
      set(s => ({...s, plans}))
    })
}))

function setCompanyUsers(s: UsersStoreState, companyId: number, users: CompanyUser[]): Partial<UsersStoreState> {
  const companyDetails = users
    .reduce((a, u) => ({...a, [u.user!.id]: u.companyDetails!}), {} as Record<string, CompanyUserDetails>)
  const updatedUsers = users.reduce((a, u) => ({...a, [u.user!.id]: u.user!}), {} as Record<string, User>)
  return {
    users: {...s.users, ...updatedUsers},
    userCompanyDetails: {...s.userCompanyDetails, [companyId]: companyDetails}
  }
}

function setOwnInfo(s: UsersStoreState, ownInfo: OwnInfo): Partial<UsersStoreState> {
  const {
    user, companyUserDetails, companyDetails, invites, limits, trialInfo, organizations, ownOrgDetails, companyLimits,
  } = ownInfo
  const updatedCompanies = companyDetails.reduce((a, c) => ({...a, [c.id]: c}), {} as Record<number, CompanyDetails>)
  const newOrgs = organizations.reduce((a, o) => ({...a, [o.id]: o}), {} as Record<string, EnterpriseOrganization>)
  return {
    users: {...s.users, [user!.id]: user!},
    ownCompanyUserDetails: companyUserDetails,
    ownInvites: invites,
    ownLimits: limits,
    ownCompanyLimits: companyLimits,
    companies: {...s.companies, ...updatedCompanies},
    ownTrialInfo: trialInfo,
    organizations: {...s.organizations, ...newOrgs},
    ownOrgDetails,
  }
}
